/**
 * EventCTAInitPanel — prepare an event's subscription CTA (voucher + QR), with tracking codes
 * copied from the promoter's (gun show) or host's (range night) organization CTA.
 *
 * Used by BOTH the create/configure wizard's last step and the event's Call to Action tab, so the
 * two surfaces cannot drift apart.
 *
 * Contract (docs/DESIGN_EIB_DEFAULT_CTA_SLICES_2026-09-25.md §4-5):
 *   - The server decides what to copy (`eib_preview_event_cta_defaults`); this component never
 *     re-derives it.
 *   - The draft is seeded ONCE per event. A refetch never overwrites what the operator typed.
 *   - "Not now" is persisted (a deferral). Reopening shows it; it never silently opts back in.
 *   - "Reload defaults" and a `source_changed` refusal both show a field-by-field comparison and
 *     write nothing until the operator applies a field.
 *   - The CTA is saved first, then the QR is generated. "Saved, QR pending" is read from the row, so it
 *     survives a reload, and retrying only regenerates the QR — it cannot create a second CTA.
 */
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QrCodeIcon,
} from "@heroicons/react/24/outline";

import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useToast } from "../ui/Toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  ctaOmitsDiscount,
  generateCTAQRCode,
  getEventCTABaseVariant,
} from "../../services/eventCTA.service";
import { INSURANCE_NO_VOUCHER_NOTE } from "../../services/ctaClassroom";
import { eventCTAKeys } from "../../hooks/useEventCTAs";
import {
  previewEventCTADefaults,
  initializeEventCTA,
  deferEventCTA,
  describeResolution,
  describeOverride,
  ceilingOf,
  describeDefaultAmount,
  classPromoNote,
  preparedClassPromoWarning,
  type CTAInitParameters,
  type CTAInitProposal,
} from "../../services/eventCTAInit.service";
import type { Event, EventCallToAction } from "../../types/event.types";
import {
  errorMessage,
  flyerEligibility,
  flyerLabel,
  FlyerFilesAccessError,
  getCTAFlyerState,
  saveCTAFlyerToEvent,
} from "../../services/eventCTAFlyer.service";
import { supabase } from "../../services/supabase";

type EditableKey =
  | "affid"
  | "promo"
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_content"
  | "altprice";

const FIELDS: { key: EditableKey; label: string; hint?: string }[] = [
  {
    key: "affid",
    label: "Affiliate ID",
    hint: "Credits signups to the partner.",
  },
  { key: "promo", label: "Promo Code" },
  { key: "utm_source", label: "UTM Source" },
  { key: "utm_medium", label: "UTM Medium" },
  { key: "utm_campaign", label: "UTM Campaign" },
  { key: "utm_content", label: "UTM Content" },
  {
    key: "altprice",
    label: "Alt Price",
    hint: "Leave blank unless you know this event needs it.",
  },
];

const INPUT =
  "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500";

const eventCTAInitKey = (eventId: string) =>
  ["event-cta-init", eventId] as const;

interface Props {
  event: Event;
  /** "wizard" explains every outcome; "tab" renders nothing when there is nothing to do. */
  surface: "wizard" | "tab";
  /**
   * False once the event has ended: a prepared CTA's summary and "Retry QR code" still show, but a new
   * preparation is not offered. Presentation only -- the server rules are the go-forward release date
   * and eligibility.
   */
  allowPrepare?: boolean;
}

const EventCTAInitPanel: React.FC<Props> = ({
  event,
  surface,
  allowPrepare = true,
}) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: preview,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: eventCTAInitKey(event.id),
    queryFn: () => previewEventCTADefaults(event.id),
    refetchOnWindowFocus: false,
  });

  // Insurance-state venue (migration 2026-10-09-410): the CTA is saved with no voucher -- the database strips it
  // (_eib_insurance_cta_no_voucher). The panel says so instead of offering an amount.
  const { data: ctaBaseVariant } = useQuery({
    queryKey: [
      "event-cta-base-variant",
      event.id,
      event.type,
      event.location_state,
    ],
    queryFn: () => getEventCTABaseVariant(event.id),
  });
  const noVoucherHere = ctaOmitsDiscount(
    { cta_type: "subscription" },
    ctaBaseVariant,
  );
  const [draft, setDraft] = useState<Record<EditableKey, string>>(emptyDraft());
  const [amount, setAmount] = useState("20");
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [comparison, setComparison] = useState<{
    proposal: CTAInitProposal;
    reason: "reload" | "source_changed";
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [qrBusy, setQrBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Hydrate ONCE per event. Keyed on the event id, set BEFORE setState so a re-render cannot re-enter.
  // A React Query refetch returns a new object; depending on it alone would clobber typed input.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    const proposal = preview?.proposal;
    if (!proposal || hydratedFor.current === event.id) {
      return;
    }
    hydratedFor.current = event.id;
    setDraft(fromParameters(proposal.parameters));
    setFingerprint(proposal.fingerprint);
    // Migration 1360: the server proposes the event's default amount (state default); the operator may change it.
    if (Number.isFinite(Number(proposal.voucher_amount))) {
      setAmount(formatAmount(Number(proposal.voucher_amount)));
    }
  }, [preview, event.id]);

  const refreshAfterWrite = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: eventCTAInitKey(event.id) }),
      queryClient.invalidateQueries({ queryKey: eventCTAKeys.list(event.id) }),
    ]);
  };

  /**
   * QR first, then the "Scan to join" flyer into the event's Files (owner, 2026-09-26: automatic after
   * preparing the CTA, go-forward only). The flyer is best-effort: the CTA and QR are already saved,
   * and "Create flyer" on the CTA card is the retry.
   */
  const generateQr = async (
    ctaId: string,
    opts: { flyerOnly?: boolean } = {},
  ): Promise<{ qr: boolean; flyer: "saved" | "skipped" | string }> => {
    if (!user?.id) {
      return { qr: false, flyer: "skipped" };
    }
    setQrBusy(true);
    let saved: EventCallToAction;
    try {
      if (opts.flyerOnly) {
        // The QR is already there; only the flyer is missing. Read the CTA as it is now.
        const { data, error: readErr } = await supabase
          .from("event_call_to_actions")
          .select("*")
          .eq("id", ctaId)
          .single();
        if (readErr) {
          throw readErr;
        }
        saved = data as EventCallToAction;
      } else {
        saved = await generateCTAQRCode(ctaId, event, user.id);
      }
    } catch (err) {
      console.warn("Event CTA QR generation failed:", err);
      setQrBusy(false);
      await refreshAfterWrite();
      return { qr: false, flyer: "skipped" };
    }
    let flyer: "saved" | "skipped" | string = "skipped";
    // Insurance-state event: the flyer shows no amount, so no voucher is needed (migration 410); a type with no insurance
    // artwork (anything but a range night or gun show) is "artwork_pending" -> no automatic flyer.
    const variant = await getEventCTABaseVariant(event.id).catch(
      () => "standard" as const,
    );
    if (flyerEligibility(saved, event, { baseVariant: variant }).ok) {
      try {
        await saveCTAFlyerToEvent({
          cta: saved,
          event,
          label: flyerLabel(saved),
          userId: user.id,
        });
        flyer = "saved";
      } catch (err) {
        // No Files access is not retryable by this user; say who can do it (audit M2).
        flyer =
          err instanceof FlyerFilesAccessError
            ? "no_access"
            : errorMessage(err);
      }
    }
    setQrBusy(false);
    await Promise.all([
      refreshAfterWrite(),
      queryClient.invalidateQueries({
        queryKey: ["event-attachments", event.id],
      }),
      queryClient.invalidateQueries({
        queryKey: ["event-cta-flyer", event.id],
      }),
    ]);
    return { qr: true, flyer };
  };

  // ------------------------------------------------ finish a prepared CTA without a click
  // Owner 2026-09-28: "everything should be done by the time they leave those wizards". Preparation
  // happens in the database when the promoter / host is linked; the QR and flyer are drawn by the
  // browser that saved the links. If that browser went away first (tab closed, connection dropped),
  // the CTA is prepared but its QR and/or flyer are missing -- finish them here, once per visit.
  const genericCta =
    preview?.status === "initialized" && !preview.initialization?.cta_deleted
      ? preview.generic_cta
      : null;
  const { data: flyerState } = useQuery({
    queryKey: ["event-cta-flyer", event.id, genericCta?.id],
    queryFn: () => getCTAFlyerState(event.id, genericCta!.id),
    enabled: !!genericCta,
    refetchOnWindowFocus: false,
  });
  const [completion, setCompletion] = useState<{
    qr: boolean;
    flyer: string;
  } | null>(null);
  const autoFinishedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!genericCta || !allowPrepare || !user?.id || flyerState === undefined) {
      return;
    }
    const needQr = !genericCta.has_url || !genericCta.has_qr;
    const needFlyer = flyerState === "missing";
    if ((!needQr && !needFlyer) || autoFinishedFor.current === genericCta.id) {
      return;
    }
    autoFinishedFor.current = genericCta.id;
    void generateQr(genericCta.id, { flyerOnly: !needQr }).then(setCompletion);
    // generateQr is recreated every render; the ref makes this run once per CTA.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    genericCta?.id,
    genericCta?.has_url,
    genericCta?.has_qr,
    flyerState,
    allowPrepare,
    user?.id,
  ]);

  const qrToast = (
    r: { qr: boolean; flyer: string },
    qrFailMessage: string,
  ) => {
    if (!r.qr) {
      showToast("warning", qrFailMessage);
    } else if (r.flyer === "saved") {
      showToast(
        "success",
        "Event CTA and QR code are ready, and the flyer is in the event's Files.",
      );
    } else if (r.flyer === "no_access") {
      showToast(
        "warning",
        "Event CTA and QR code are ready. The flyer wasn't saved because you don't have access to this event's Files — someone who does (an admin or manager, or a teammate this event is shared with) can use \"Create flyer\" on the CTA.",
      );
    } else if (r.flyer === "skipped") {
      showToast("success", "Event CTA and QR code are ready.");
    } else {
      showToast(
        "warning",
        `Event CTA and QR code are ready, but the flyer wasn't saved: ${r.flyer} Use "Create flyer" on the CTA.`,
      );
    }
  };

  const handleSave = async () => {
    setFormError(null);
    const voucherAmount = Number(amount);
    if (!Number.isFinite(voucherAmount) || voucherAmount <= 0) {
      setFormError("Enter a voucher amount greater than $0.");
      return;
    }
    // Mig 1480: the state offer is a ceiling (the server refuses more, too).
    const ceiling = ceilingOf(preview?.default_amount);
    if (ceiling !== null && voucherAmount > ceiling) {
      setFormError(
        `The most this event can give is $${formatAmount(ceiling)} (the Event in a Box offer for ${preview?.default_amount?.state ?? "this state"}). You can lower the amount, not raise it.`,
      );
      return;
    }
    if (!fingerprint) {
      return;
    }
    setSaving(true);
    try {
      const result = await initializeEventCTA({
        eventId: event.id,
        fingerprint,
        parameters: toParameters(draft),
        voucherAmount,
      });
      switch (result.status) {
        case "initialized": {
          qrToast(
            await generateQr(result.event_cta_id),
            "CTA saved, but the QR code wasn't generated. Use Retry QR code.",
          );
          break;
        }
        case "source_changed":
          // Nothing was written. The draft stays exactly as typed.
          setComparison({
            proposal: result.proposal,
            reason: "source_changed",
          });
          break;
        case "already_initialized":
          showToast("info", "This event's CTA was already prepared.");
          await refreshAfterWrite();
          break;
        case "not_pristine":
          showToast(
            "info",
            "This event's CTA has already been customized, so it wasn't changed.",
          );
          await refreshAfterWrite();
          break;
        case "invalid_amount":
          setFormError("Enter a voucher amount between $0 and $1,000.");
          break;
        case "invalid_parameters":
          setFormError(
            "One of the tracking codes is too long (200 characters max).",
          );
          break;
        default:
          setFormError(refusalMessage(result.status));
      }
    } catch (err) {
      // A thrown error can follow a COMMIT (lost response), so "nothing was changed" is not knowable
      // here. Re-read and say what actually happened (round-3 audit: the same pattern broke QR and
      // flyer cleanup).
      console.error("Error preparing event CTA:", err);
      // Mig 1480: the server's ceiling refusal rolls the whole save back; say why.
      const msg = (err as { message?: string } | null)?.message ?? "";
      if (msg.includes("EIB_STATE_OFFER_CEILING")) {
        setFormError(msg.replace(/^EIB_STATE_OFFER_CEILING:\s*/, ""));
        return;
      }
      const fresh = await refetch().catch(() => null);
      if (fresh?.data?.status === "initialized") {
        showToast(
          "warning",
          "The CTA was saved, but the QR code wasn't generated. Use Retry QR code.",
        );
      } else if (fresh?.data) {
        setFormError("Couldn't save the CTA. Try again.");
      } else {
        setFormError(
          "Couldn't confirm whether the CTA was saved. Reload the page to check before trying again.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDefer = async () => {
    setSaving(true);
    try {
      const r = await deferEventCTA(event.id);
      if (r.status === "forbidden" || r.status === "event_gone") {
        setFormError(refusalMessage(r.status));
      } else {
        setExpanded(false);
        await refreshAfterWrite();
      }
    } catch (err) {
      console.error("Error deferring event CTA:", err);
      const fresh = await refetch().catch(() => null);
      if (fresh?.data?.status === "deferred") {
        setExpanded(false);
      } else if (fresh?.data) {
        setFormError("Couldn't save your choice. Try again.");
      } else {
        setFormError(
          "Couldn't confirm whether your choice was saved. Reload the page to check.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    const fresh = await refetch();
    if (fresh.data?.proposal) {
      setComparison({ proposal: fresh.data.proposal, reason: "reload" });
    }
  };

  const applyField = (key: EditableKey, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const closeComparison = () => {
    // The operator has now SEEN the current source; saving against it is an informed choice.
    if (comparison) {
      setFingerprint(comparison.proposal.fingerprint);
    }
    setComparison(null);
  };

  // ---------------------------------------------------------------- render
  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !preview) {
    // A load failure is NOT "no source" — say so, and let them retry.
    return (
      <Shell>
        <p className="text-sm text-red-700">
          Couldn&apos;t load the CTA settings for this event.
        </p>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </Shell>
    );
  }

  const { status } = preview;

  if (status === "forbidden" || status === "event_gone") {
    return surface === "wizard" ? (
      <Shell>
        <p className="text-sm text-gray-600">{refusalMessage(status)}</p>
      </Shell>
    ) : null;
  }
  if (status === "predates_release") {
    // Go-forward only (owner, 2026-09-26): events created before the release are not prepared.
    return surface === "wizard" ? (
      <Shell>
        <p className="text-sm text-gray-600">
          This event was created before automatic CTA preparation was released,
          so it isn&apos;t prepared here. Its CTAs can still be added or edited
          on the event&apos;s <strong>Call to Action</strong> tab.
        </p>
      </Shell>
    ) : null;
  }
  if (status === "not_eligible") {
    return surface === "wizard" ? (
      <Shell>
        <p className="text-sm text-gray-600">
          This event isn&apos;t set up for subscription signups, so there&apos;s
          no subscription CTA to prepare.
        </p>
      </Shell>
    ) : null;
  }
  if (status === "not_pristine") {
    return surface === "wizard" ? (
      <Shell>
        <p className="text-sm text-gray-600">
          This event&apos;s CTA has already been customized. Review or edit it
          on the event&apos;s <strong>Call to Action</strong> tab.
        </p>
      </Shell>
    ) : null;
  }

  if (status === "initialized" && preview.initialization?.cta_deleted) {
    // At most once per event (audit M3): the prepared CTA was deleted, and it is not re-offered.
    return (
      <Shell>
        <p className="text-sm text-gray-600">
          This event&apos;s CTA was prepared
          {preview.initialization.initialized_at &&
            ` on ${new Date(preview.initialization.initialized_at).toLocaleDateString()}`}{" "}
          and later deleted. Automatic preparation runs once per event — add a
          CTA on this tab if you need one.
        </p>
      </Shell>
    );
  }

  if (status === "initialized") {
    const init = preview.initialization;
    const qrPending =
      !!preview.generic_cta &&
      (!preview.generic_cta.has_url || !preview.generic_cta.has_qr);
    return (
      <Shell>
        <div className="flex items-start gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-700">
            <p className="font-medium text-gray-900">
              Event CTA prepared
              {noVoucherHere
                ? " — no voucher (insurance-state venue)"
                : init?.voucher_amount !== null &&
                  init?.voucher_amount !== undefined &&
                  ` — $${formatAmount(init.voucher_amount)} voucher`}
            </p>
            <p className="text-gray-600">
              {init?.source_organization_name
                ? `Tracking codes copied from ${init.source_organization_name}${init.source_location_name ? ` (${init.source_location_name})` : ""}${
                    // mig 1712: a gun show whose promoter has no CTA uses the state's No Reseller codes
                    init.resolution === "state_no_reseller"
                      ? ", the state's No Reseller codes (the promoter has no CTA of its own)"
                      : ""
                  }`
                : "No tracking codes were copied"}
              {init?.initialized_at &&
                ` on ${new Date(init.initialized_at).toLocaleDateString()}`}
              {init?.actor_name && ` by ${init.actor_name}`}.
              {surface === "wizard" &&
                " Edit it any time on the event's Call to Action tab."}
            </p>
          </div>
        </div>
        {preparedClassPromoWarning(preview.class_promo) && (
          <div
            className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            data-testid="cta-init-class-promo-warning"
          >
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            <span>{preparedClassPromoWarning(preview.class_promo)}</span>
          </div>
        )}
        {qrBusy && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <LoadingSpinner size="sm" />
            <span>Finishing the QR code and flyer…</span>
          </div>
        )}
        {!qrBusy && !qrPending && preview.generic_cta && (
          <ul
            className="space-y-1 text-sm"
            data-testid="event-cta-init-outputs"
          >
            <li className="flex items-center gap-2 text-gray-700">
              <CheckCircleIcon className="h-4 w-4 text-green-600 shrink-0" />
              QR code ready
            </li>
            {flyerState === "saved" && (
              <li className="flex items-center gap-2 text-gray-700">
                <CheckCircleIcon className="h-4 w-4 text-green-600 shrink-0" />
                &ldquo;Scan to join&rdquo; flyer saved to the event&apos;s Files
              </li>
            )}
            {flyerState === "removed" && (
              <li className="text-gray-600">
                This CTA&apos;s flyer was removed from Files. Use &ldquo;Create
                flyer&rdquo; on the CTA to make a new one.
              </li>
            )}
            {flyerState === "no_access" && (
              <li className="text-gray-600">
                The flyer needs someone with access to this event&apos;s Files
                (an admin or manager, or a teammate this event is shared with).
                They can use &ldquo;Create flyer&rdquo; on the CTA.
              </li>
            )}
            {flyerState === "missing" &&
              completion &&
              completion.flyer !== "saved" &&
              completion.flyer !== "skipped" && (
                <li className="text-yellow-800">
                  The flyer wasn&apos;t saved
                  {completion.flyer !== "no_access" &&
                    `: ${completion.flyer.replace(/\.$/, "")}`}
                  . Use &ldquo;Create flyer&rdquo; on the CTA.
                </li>
              )}
          </ul>
        )}
        {!qrBusy && qrPending && preview.generic_cta && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-yellow-200 bg-yellow-50 p-3">
            <p className="text-sm text-yellow-800">
              CTA saved — QR code pending.
            </p>
            <Button
              size="sm"
              variant="secondary"
              isLoading={qrBusy}
              onClick={async () => {
                qrToast(
                  await generateQr(preview.generic_cta!.id),
                  "QR code still couldn't be generated.",
                );
              }}
            >
              <QrCodeIcon className="h-4 w-4 mr-1" /> Retry QR code
            </Button>
          </div>
        )}
      </Shell>
    );
  }

  // status: "ready" | "deferred"
  if (!allowPrepare) {
    return null;
  }
  const proposal = preview.proposal!;
  const deferred = status === "deferred";
  const showForm = !deferred || expanded;
  const missing = (["affid", "promo"] as const).filter((k) => !draft[k].trim());

  return (
    <Shell>
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          Subscription CTA &amp; QR code
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Creates the QR code attendees scan to sign up
          {noVoucherHere ? "" : " with a voucher"}. Tracking codes are filled in
          for you so the signup is credited to the right partner. You can change
          any of them.
        </p>
      </div>

      {deferred && !expanded && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm text-gray-700">
            Not prepared — {preview.initialization?.actor_name ?? "someone"}{" "}
            chose &ldquo;Not now&rdquo;
            {preview.initialization?.deferred_at &&
              ` on ${new Date(preview.initialization.deferred_at).toLocaleDateString()}`}
            .
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setExpanded(true)}
          >
            Prepare it now
          </Button>
        </div>
      )}

      {showForm && (
        <>
          <SourceSummary proposal={proposal} />
          <ClassPromoNote proposal={proposal} />

          {(preview.generic_cta?.has_url || preview.generic_cta?.has_qr) && (
            <p
              className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
              data-testid="cta-init-published-note"
            >
              This event&apos;s CTA already has a QR code, which may be printed,
              so it isn&apos;t set up automatically. Preparing it here replaces
              that QR code and flyer.
            </p>
          )}

          {comparison && (
            <Comparison
              current={draft}
              proposal={comparison.proposal}
              reason={comparison.reason}
              previewAmount={proposal.voucher_amount}
              onApply={applyField}
              onApplyAll={() => {
                setDraft(fromParameters(comparison.proposal.parameters));
                closeComparison();
              }}
              onClose={closeComparison}
            />
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {noVoucherHere ? (
              <p
                className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
                data-testid="cta-no-voucher-note"
              >
                {INSURANCE_NO_VOUCHER_NOTE}
              </p>
            ) : (
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="cta-init-amount"
                >
                  Voucher amount ($)
                </label>
                <input
                  id="cta-init-amount"
                  type="number"
                  min="1"
                  max={ceilingOf(preview.default_amount) ?? undefined}
                  step="1"
                  className={INPUT}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                {preview.default_amount && (
                  <p
                    className="text-xs text-gray-500 mt-0.5"
                    data-testid="cta-init-default-amount"
                  >
                    {describeDefaultAmount(preview.default_amount)}
                    {ceilingOf(preview.default_amount) === null
                      ? " You can change it."
                      : ""}
                  </p>
                )}
              </div>
            )}
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">
                UTM Term
              </span>
              <p className="px-3 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md">
                {proposal.event_state ?? "— (the event has no state)"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Always the event&apos;s state.
              </p>
            </div>
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor={`cta-init-${f.key}`}
                >
                  {f.label}
                </label>
                <input
                  id={`cta-init-${f.key}`}
                  className={INPUT}
                  value={draft[f.key]}
                  onChange={(e) => applyField(f.key, e.target.value)}
                />
                {f.key === "altprice" &&
                  proposal.needs_confirmation.altprice &&
                  !draft.altprice && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      The source CTA uses {proposal.needs_confirmation.altprice}
                      .{" "}
                      <button
                        type="button"
                        className="text-primary-600 hover:underline"
                        onClick={() =>
                          applyField(
                            "altprice",
                            proposal.needs_confirmation.altprice!,
                          )
                        }
                      >
                        Use it
                      </button>
                    </p>
                  )}
                {f.hint &&
                  !(
                    f.key === "altprice" && proposal.needs_confirmation.altprice
                  ) && <p className="text-xs text-gray-500 mt-0.5">{f.hint}</p>}
              </div>
            ))}
          </div>

          {missing.length > 0 && (
            <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              <span>
                Without an {missing.map(labelFor).join(" and ")}, signups from
                this QR code won&apos;t be credited to a partner.
              </span>
            </div>
          )}

          {formError && <p className="text-sm text-red-700">{formError}</p>}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              className="text-sm text-primary-600 hover:underline"
              onClick={handleReload}
            >
              Compare with the current source
            </button>
            <div className="flex gap-3">
              {!deferred && (
                <Button
                  variant="secondary"
                  onClick={handleDefer}
                  disabled={saving}
                >
                  Not now
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={saving || qrBusy}
              >
                Prepare CTA &amp; QR code
              </Button>
            </div>
          </div>
        </>
      )}
      {formError && !showForm && (
        <p className="text-sm text-red-700">{formError}</p>
      )}
    </Shell>
  );
};

// ------------------------------------------------------------------ pieces
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="rounded-lg border border-gray-200 bg-white p-4 space-y-4"
    data-testid="event-cta-init-panel"
  >
    {children}
  </div>
);

/** Migration 1580: the CLASS promo rule's warning / confirmation for a range night in a voucher state. */
const ClassPromoNote: React.FC<{ proposal: CTAInitProposal }> = ({
  proposal,
}) => {
  const note = classPromoNote(proposal);
  if (!note) {
    return null;
  }
  return note.tone === "warning" ? (
    <div
      className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
      data-testid="cta-init-class-promo-warning"
    >
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
      <span>{note.text}</span>
    </div>
  ) : (
    <p
      className="text-xs text-gray-600"
      data-testid="cta-init-class-promo-note"
    >
      {note.text}
    </p>
  );
};

const SourceSummary: React.FC<{ proposal: CTAInitProposal }> = ({
  proposal,
}) => (
  <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700 space-y-1">
    <p>{describeResolution(proposal)}</p>
    {proposal.source && (
      <p className="text-gray-600">
        Source: <strong>{proposal.source.name}</strong>
        {proposal.source.location_name && (
          <>
            {" "}
            for <strong>{proposal.source.location_name}</strong>
          </>
        )}
        {", created "}
        {new Date(proposal.source.created_at).toLocaleDateString()}.
      </p>
    )}
    {proposal.derived_overrides.map((o, i) => (
      <p key={i} className="text-gray-600">
        {describeOverride(o)}
      </p>
    ))}
  </div>
);

const Comparison: React.FC<{
  current: Record<EditableKey, string>;
  proposal: CTAInitProposal;
  reason: "reload" | "source_changed";
  /** The amount the form's defaults were loaded for (the preview). */
  previewAmount: number;
  onApply: (key: EditableKey, value: string) => void;
  onApplyAll: () => void;
  onClose: () => void;
}> = ({
  current,
  proposal,
  reason,
  previewAmount,
  onApply,
  onApplyAll,
  onClose,
}) => {
  const proposed = fromParameters(proposal.parameters);
  const rows = FIELDS.filter(
    (f) => (current[f.key] ?? "") !== (proposed[f.key] ?? ""),
  );
  return (
    <div
      className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2"
      data-testid="event-cta-init-comparison"
    >
      <p className="text-sm text-blue-900">
        {comparisonMessage(
          reason,
          rows.length,
          Number(proposal.voucher_amount) !== Number(previewAmount)
            ? {
                loaded: Number(previewAmount),
                saved: Number(proposal.voucher_amount),
              }
            : null,
        )}
      </p>
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-blue-900">
              <th className="py-1 pr-2">Field</th>
              <th className="py-1 pr-2">Yours</th>
              <th className="py-1 pr-2">Current source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.key} className="border-t border-blue-100">
                <td className="py-1 pr-2">{f.label}</td>
                <td className="py-1 pr-2 font-mono">{current[f.key] || "—"}</td>
                <td className="py-1 pr-2 font-mono">
                  {proposed[f.key] || "—"}
                </td>
                <td className="py-1 text-right">
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() => onApply(f.key, proposed[f.key] ?? "")}
                  >
                    Use source
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex justify-end gap-3">
        {rows.length > 0 && (
          <Button size="sm" variant="secondary" onClick={onApplyAll}>
            Use all from source
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={onClose}>
          {rows.length > 0 ? "Keep mine" : "Close"}
        </Button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ helpers
function comparisonMessage(
  reason: "reload" | "source_changed",
  differing: number,
  /** Set when the save used a different voucher amount than the defaults were loaded for (migration 440). */
  amounts: { loaded: number; saved: number } | null = null,
) {
  if (reason === "source_changed" && amounts) {
    // Since migration 440 the store CTA to copy from must have the same voucher amount, so changing the
    // amount changes the source. The source itself did not change; say what did.
    return `Tracking codes come from the store's CTA with the same voucher amount when there is one (otherwise from its CTA with tracking codes). These defaults were loaded for $${formatAmount(amounts.loaded)}, and you're saving $${formatAmount(amounts.saved)}, so nothing was saved yet. Here's what's different for $${formatAmount(amounts.saved)} — apply what you want, then save again. A campaign code that belongs to another amount is removed when you save.`;
  }
  if (reason === "source_changed") {
    return "The source CTA changed after this form loaded, so nothing was saved. Here's what's different — apply what you want, then save again.";
  }
  if (differing === 0) {
    return "Your values match the current source.";
  }
  return "Here's how your values differ from the current source. Nothing changes unless you apply it.";
}

function emptyDraft(): Record<EditableKey, string> {
  return {
    affid: "",
    promo: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    altprice: "",
  };
}

function fromParameters(p: CTAInitParameters): Record<EditableKey, string> {
  const d = emptyDraft();
  for (const f of FIELDS) {
    d[f.key] = p[f.key] ?? "";
  }
  return d;
}

/** Only non-empty values are sent: an explicit clear is an edit, and the server does not refill it. */
function toParameters(d: Record<EditableKey, string>): CTAInitParameters {
  const out: CTAInitParameters = {};
  for (const f of FIELDS) {
    const v = d[f.key].trim();
    if (v) {
      out[f.key] = v;
    }
  }
  return out;
}

function labelFor(key: string) {
  return FIELDS.find((f) => f.key === key)?.label ?? key;
}

function formatAmount(n: number) {
  return Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2);
}

function refusalMessage(status: string) {
  switch (status) {
    case "forbidden":
      return "You don't have permission to change this event's CTAs.";
    case "event_gone":
      return "This event no longer exists.";
    case "not_eligible":
      return "This event isn't set up for subscription signups.";
    default:
      return "Couldn't save the CTA.";
  }
}

export default EventCTAInitPanel;
